import React, { createContext, useContext, useRef, useState, ReactNode, useCallback } from 'react';

// Session data structure
export interface SessionData {
  sessionId: string;
  messages: any[]; // Message array from ChatPage
  contextTags: string[];
  orderId?: number | null;
  lastUpdated: number;
}

interface SessionContextType {
  // Primary session management
  activeSessionId: string;
  setActiveSessionId: (sessionId: string) => void;
  removeActiveSessionId: () => void;
  
  // Session cache management
  getSessionData: (sessionId: string) => SessionData | null;
  setSessionData: (sessionId: string, data: Partial<SessionData>) => void;
  clearSessionData: (sessionId: string) => void;
  clearAllSessions: () => void;
  
  // Session tracking refs
  lastLoadedSessionRef: React.MutableRefObject<string>;
  activeAbortControllerRef: React.MutableRefObject<AbortController | null>;
}

const SessionContext = createContext<SessionContextType | undefined>(undefined);

export const useSession = (): SessionContextType => {
  const context = useContext(SessionContext);
  if (context === undefined) {
    throw new Error('useSession must be used within a SessionProvider');
  }
  return context;
};

/** Non-throwing accessor for the active session id. Returns '' when used outside
 *  a SessionProvider (e.g. guest mode), so components that only NEED the id for an
 *  optional feature can degrade gracefully instead of crashing. */
export const useActiveSessionIdSafe = (): string => {
  const context = useContext(SessionContext);
  return context?.activeSessionId ?? '';
};

interface SessionProviderProps {
  children: ReactNode;
}

export const SessionProvider: React.FC<SessionProviderProps> = ({ children }) => {
  // Active session state
  const [activeSessionId, setActiveSessionId] = useState<string>('');
  
  // Session cache - stores session data by session ID
  const [sessionCache, setSessionCache] = useState<Map<string, SessionData>>(new Map());

  // Track the last loaded session
  const lastLoadedSessionRef = useRef<string>('');
  
  const removeActiveSessionId = () => {
    setActiveSessionId('');
  };

  // Track abort controller for cleanup
  const activeAbortControllerRef = useRef<AbortController | null>(null);

  // Get cached session data
  const getSessionData = useCallback((sessionId: string): SessionData | null => {
    return sessionCache.get(sessionId) || null;
  }, [sessionCache]);

  // Update session cache
  const setSessionData = useCallback((sessionId: string, data: Partial<SessionData>) => {
    setSessionCache(prev => {
      const newCache = new Map(prev);
      const existing = newCache.get(sessionId);
      
      newCache.set(sessionId, {
        sessionId,
        messages: data.messages ?? existing?.messages ?? [],
        contextTags: data.contextTags ?? existing?.contextTags ?? [],
        orderId: data.orderId !== undefined ? data.orderId : existing?.orderId,
        lastUpdated: Date.now(),
      });
      
      return newCache;
    });
  }, []);

  // Clear specific session from cache
  const clearSessionData = useCallback((sessionId: string) => {
    setSessionCache(prev => {
      const newCache = new Map(prev);
      newCache.delete(sessionId);
      return newCache;
    });
  }, []);

  // Clear all sessions from cache
  const clearAllSessions = useCallback(() => {
    setSessionCache(new Map());
  }, []);

  return (
    <SessionContext.Provider 
      value={{ 
        activeSessionId,
        setActiveSessionId,
        removeActiveSessionId,
        getSessionData,
        setSessionData,
        clearSessionData,
        clearAllSessions,
        lastLoadedSessionRef,
        activeAbortControllerRef,
      }}
    >
      {children}
    </SessionContext.Provider>
  );
};
